import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import EditNoteIcon from "@mui/icons-material/EditNote";
import { MarkdownContent } from "../../../components/chat/MarkdownContent";
import { EntityReviewPanel } from "./EntityReviewPanel";
import { getAwaitingInputBadgeSlotSx } from "../../../utils/awaitingInputStyles";
import type { NormalizedPlanPresentation, ReviewEntity, ReviewGap } from "../types";

interface PlanApprovalCardProps {
  plan: NormalizedPlanPresentation;
  feedback: string;
  onFeedbackChange: (value: string) => void;
  onApprove: () => void | Promise<void>;
  onRequestChanges: () => void | Promise<void>;
  isSubmitting: boolean;
  isAwaitingRevision: boolean;
  /**
   * True iff the officer has touched the entity outline since the
   * current plan loaded. Required so pure entity-only edits (no typed
   * feedback, no resolved gaps) can still drive the Request Changes
   * button — without this flag, hasOfficerInput would stay false and
   * the entity-override revision path would be unreachable. Defaults
   * to false for legacy callers that haven't been updated.
   */
  entitiesEdited?: boolean;
  entities: ReviewEntity[];
  gaps: ReviewGap[];
  onUpdateEntity: (id: string, patch: Partial<ReviewEntity>) => void;
  onRemoveEntity: (id: string) => void;
  onRestoreEntity: (id: string) => void;
  onAddEntity: (category: string) => void;
  onReorderEntities: (category: string, fromIndex: number, toIndex: number) => void;
  onResolveGap: (id: string, resolution: string) => void;
}

export const PlanApprovalCard: React.FC<PlanApprovalCardProps> = ({
  plan,
  feedback,
  onFeedbackChange,
  onApprove,
  onRequestChanges,
  isSubmitting,
  isAwaitingRevision,
  entitiesEdited = false,
  entities,
  gaps,
  onUpdateEntity,
  onRemoveEntity,
  onRestoreEntity,
  onAddEntity,
  onReorderEntities,
  onResolveGap,
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const [confirmApproveOpen, setConfirmApproveOpen] = useState(false);
  // Tracks whether the reviewer has at least visited the "Entities &
  // Awaiting Clarification" tab. When the AI flagged unresolved gaps we
  // require this visit before letting them approve, so the officer is
  // forced to acknowledge the gaps before signing off. Resolution is not
  // required — the existing confirmation dialog still gates that.
  const [hasVisitedClarificationTab, setHasVisitedClarificationTab] =
    useState(false);
  const awaitingInputBadgeSlotSx = getAwaitingInputBadgeSlotSx();
  const hasEntities = entities.length > 0 || gaps.length > 0;
  const actionsDisabled = isSubmitting || isAwaitingRevision;
  const hasClarificationResponses = useMemo(
    () => gaps.some((gap) => Boolean(gap.resolution?.trim())),
    [gaps]
  );
  const hasUnresolvedGaps = useMemo(
    () => gaps.some((gap) => !gap.resolution?.trim()),
    [gaps]
  );
  // "Has anything to send back" — used to gate the Request Changes
  // button. The Approve button is independent: it always carries
  // whatever's typed/edited/resolved alongside { approved: true } via
  // the hook's submitApproval composer.
  // Pure entity-only edits (officer adds/removes/reorders entities
  // without typing feedback or resolving a gap) must also enable
  // Request Changes so the override revision path is reachable.
  const hasOfficerInput =
    entitiesEdited ||
    feedback.trim().length > 0 ||
    hasClarificationResponses;
  // Approve gating: only the unresolved-gap + unvisited-tab condition.
  // Typing feedback or resolving gaps must NOT make Approve unreachable —
  // the submitApproval composer already ships those fields to the
  // orchestrator on approve.
  const mustVisitClarificationTab =
    hasUnresolvedGaps && !hasVisitedClarificationTab;
  const approveBlockedByTabGate = mustVisitClarificationTab;

  // Reset per-outline UI state when a fresh plan arrives (revised plan
  // after Request Changes, or a different interrupt). Without this, the
  // tab-visit gate would inherit `true` from the previous outline so the
  // new gate would never engage.
  useEffect(() => {
    setHasVisitedClarificationTab(false);
    setActiveTab(0);
  }, [plan]);

  const handleTabChange = (_: unknown, value: number) => {
    setActiveTab(value);
    if (value === 1) {
      setHasVisitedClarificationTab(true);
    }
  };

  const handleApprove = () => {
    if (actionsDisabled || approveBlockedByTabGate) return;
    if (hasUnresolvedGaps) {
      setConfirmApproveOpen(true);
      return;
    }
    void onApprove();
  };

  const handleRequestChanges = () => {
    if (actionsDisabled) return;
    void onRequestChanges();
  };

  return (
    <Card variant="outlined">
      <CardContent sx={{ p: { xs: 2, md: 3 } }}>
        <Stack spacing={3}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
              {plan.title}
            </Typography>
            {plan.summary && (
              <Typography variant="body2" color="text.secondary">
                {plan.summary}
              </Typography>
            )}
          </Box>

          {isAwaitingRevision && (
            <Alert severity="info">
              AI Assistant is revising the outline with your feedback.
            </Alert>
          )}

          {hasEntities ? (
            <>
              <Tabs
                value={activeTab}
                onChange={handleTabChange}
                sx={{ borderBottom: 1, borderColor: "divider" }}
              >
                <Tab label="Outline" />
                <Tab
                  label={
                    <Badge
                      badgeContent={gaps.length || undefined}
                      color="default"
                      sx={{
                        "& .MuiBadge-badge": {
                          ...awaitingInputBadgeSlotSx,
                          fontSize: "0.65rem",
                          height: 16,
                          minWidth: 16,
                        },
                      }}
                    >
                      <Box sx={{ pr: gaps.length ? 1.5 : 0 }}>
                        Entities &amp; Awaiting Clarification
                      </Box>
                    </Badge>
                  }
                />
              </Tabs>

              {activeTab === 0 && (
                <Box
                  sx={{
                    border: 1,
                    borderColor: "divider",
                    borderRadius: 2,
                    p: { xs: 2, md: 2.5 },
                    bgcolor: "background.paper",
                  }}
                >
                  <MarkdownContent content={plan.markdown} />
                </Box>
              )}

              {activeTab === 1 && (
                <Box
                  sx={{
                    border: 1,
                    borderColor: "divider",
                    borderRadius: 2,
                    p: { xs: 2, md: 2.5 },
                    bgcolor: "background.paper",
                  }}
                >
                  <EntityReviewPanel
                    entities={entities}
                    gaps={gaps}
                    onUpdateEntity={onUpdateEntity}
                    onRemoveEntity={onRemoveEntity}
                    onRestoreEntity={onRestoreEntity}
                    onAddEntity={onAddEntity}
                    onReorderEntities={onReorderEntities}
                    onResolveGap={onResolveGap}
                    disabled={actionsDisabled}
                  />
                </Box>
              )}
            </>
          ) : (
            <Box
              sx={{
                border: 1,
                borderColor: "divider",
                borderRadius: 2,
                p: { xs: 2, md: 2.5 },
                bgcolor: "background.paper",
              }}
            >
              <MarkdownContent content={plan.markdown} />
            </Box>
          )}

          <TextField
            label="Request changes"
            placeholder="Explain what the AI Assistant should change before generation starts."
            value={feedback}
            onChange={(event) => onFeedbackChange(event.target.value)}
            multiline
            minRows={4}
            fullWidth
            disabled={isSubmitting}
          />

          {/* Two distinct actions, both always visible. The officer
              picks the path explicitly: Request Changes routes to the
              planner for revision; Approve sends officer comments,
              entity edits, and gap clarifications to the orchestrator
              and proceeds with extraction. Typing feedback or resolving
              gaps no longer auto-flips the primary button — Approve
              stays reachable. */}
          <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={2} flexWrap="wrap" useFlexGap>
            {approveBlockedByTabGate && (
              <Typography variant="caption" color="text.secondary">
                Review the &quot;Entities &amp; Awaiting Clarification&quot; tab before approving.
              </Typography>
            )}
            <Button
              variant="outlined"
              color="warning"
              startIcon={<EditNoteIcon />}
              onClick={handleRequestChanges}
              disabled={actionsDisabled || !hasOfficerInput}
            >
              {isAwaitingRevision ? "Revising..." : "Request Changes"}
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={<CheckCircleOutlineIcon />}
              onClick={handleApprove}
              disabled={actionsDisabled || approveBlockedByTabGate}
            >
              Approve Outline
            </Button>
          </Stack>
        </Stack>
      </CardContent>

      <Dialog
        open={confirmApproveOpen}
        onClose={() => setConfirmApproveOpen(false)}
      >
        <DialogTitle>Approve Outline?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to approve this outline without clarifying the AI Assistant&apos;s questions?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmApproveOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => {
              setConfirmApproveOpen(false);
              void onApprove();
            }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};
